import { useEffect, useRef, useState } from "react";
import { getDeferredPreQuote } from "../services/api";
import type { IEstimateResult } from "../services/api";

const DEBOUNCE_MS = 500;
const cache = new Map<string, IEstimateResult>();
const inFlight = new Map<string, Promise<IEstimateResult>>();

export const clearPreQuoteCache = (): void => {
  cache.clear();
  inFlight.clear();
};

const stableKey = (linkId: string, payoutMethod?: string): string =>
  `${linkId}|${payoutMethod ?? "*"}`;

const fetchWithCache = (
  key: string,
  linkId: string,
  payoutMethod?: string
): Promise<IEstimateResult> => {
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);
  const existing = inFlight.get(key);
  if (existing) return existing;
  const promise = getDeferredPreQuote(linkId, payoutMethod ? { payoutMethod } : undefined)
    .then((result) => {
      cache.set(key, result);
      inFlight.delete(key);
      return result;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
};

export interface UseDeferredPreQuoteState {
  result: IEstimateResult | null;
  loading: boolean;
  error: Error | null;
}

export const useDeferredPreQuote = (
  linkId: string | null | undefined,
  payoutMethod?: string | null
): UseDeferredPreQuoteState => {
  const key = linkId ? stableKey(linkId, payoutMethod ?? undefined) : null;

  const [state, setState] = useState<UseDeferredPreQuoteState>(() => {
    if (!key) return { result: null, loading: false, error: null };
    const cached = cache.get(key);
    return cached
      ? { result: cached, loading: false, error: null }
      : { result: null, loading: true, error: null };
  });

  const linkRef = useRef(linkId);
  linkRef.current = linkId;
  const payoutRef = useRef(payoutMethod);
  payoutRef.current = payoutMethod;

  useEffect(() => {
    if (!key || !linkRef.current) {
      setState((prev) =>
        prev.result == null && !prev.loading && prev.error == null
          ? prev
          : { result: null, loading: false, error: null }
      );
      return;
    }

    const cached = cache.get(key);
    if (cached) {
      setState((prev) =>
        prev.result === cached && !prev.loading && prev.error == null
          ? prev
          : { result: cached, loading: false, error: null }
      );
      return;
    }

    let cancelled = false;
    setState((s) =>
      s.loading && s.error == null ? s : { ...s, loading: true, error: null }
    );

    const timer = window.setTimeout(() => {
      fetchWithCache(
        key,
        linkRef.current as string,
        payoutRef.current ?? undefined
      )
        .then((result) => {
          if (!cancelled) setState({ result, loading: false, error: null });
        })
        .catch((err: Error) => {
          if (!cancelled) setState({ result: null, loading: false, error: err });
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [key]);

  return state;
};
